import React from "react";
import ParticleField from "../ParticleField";
import { Rays } from "../Atmosphere";
import { Palette } from "../holidayConfig";
import { circlePath, Grain, Layer, mix, ridge } from "./kit";
import { Bunny, Chick, Daffodil, Egg, EggPattern, HangingEgg, LEAF, Witch } from "./EasterCharacters";
import { Shaded, ShadeColors, ToonDefs } from "./toon";
import { range, seeded } from "./sceneUtils";
import "./scenes.css";

/*
 * Påsk: en solig vårmorgon. Solen strålar över böljande rapsfält och en
 * gul stuga på kullen, påskkärringen flyger förbi med kaffepannan,
 * harar och kycklingar leker bland målade ägg och påskliljor, och
 * påskägg hänger och dinglar från skärmens överkant.
 */

const Cloud: React.FC<{ p: Palette }> = ({ p }) => (
  <svg viewBox="-80 -50 170 70">
    <Shaded
      d="M-70 14 C -80 14, -80 -4, -64 -6 C -64 -26, -38 -32, -26 -18 C -20 -40, 14 -44, 24 -20 C 36 -30, 60 -24, 58 -6 C 76 -6, 80 14, 64 14 Z"
      c={{ base: "#ffffff", shade: mix("#ffffff", p[200], 0.55), light: "#ffffff" }}
      depth={9}
    />
  </svg>
);

const Cottage: React.FC<{ x: number; y: number; s?: number; p: Palette }> = ({ x, y, s = 1, p }) => {
  const wall: ShadeColors = { base: p[300], shade: p[400], light: p[200] };
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <rect x="-44" y="-46" width="88" height="48" fill={wall.base} />
      <rect x="10" y="-46" width="34" height="48" fill={wall.shade} opacity="0.5" />
      <path d="M-52 -44 L 0 -80 L 52 -44 Z" fill={mix(p[600], "#9a6a4a", 0.35)} />
      <path d="M-52 -44 L 0 -80 L 4 -77 L -46 -42 Z" fill="#ffffff" opacity="0.25" />
      <rect x="18" y="-92" width="9" height="20" fill="#ffffff" />
      <path className="hol-smoke" d="M22 -96 c 6 -8, -4 -12, 2 -20" stroke="#ffffff" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.8" />
      {[-30, 14].map(wx => (
        <g key={wx}>
          <rect x={wx} y="-34" width="16" height="16" fill="#ffffff" />
          <rect x={wx + 2} y="-32" width="12" height="12" fill={mix(p[100], "#cfe3ff", 0.3)} />
          <path d={`M${wx + 8} -32 V -20 M ${wx + 2} -26 H ${wx + 14}`} stroke="#ffffff" strokeWidth="1.5" />
        </g>
      ))}
      <rect x="-8" y="-30" width="14" height="32" fill="#ffffff" />
      <rect x="-6" y="-28" width="10" height="30" fill={mix(p[500], "#9a6a4a", 0.2)} />
      <path d="M-44 -46 V 2 M 44 -46 V 2" stroke="#ffffff" strokeWidth="4" />
      {/* Flaggstång */}
      <path d="M70 2 V -96" stroke="#ffffff" strokeWidth="2.5" />
      <path d="M71 -95 C 84 -92, 92 -96, 104 -92 C 94 -90, 86 -86, 71 -88 Z" fill={p[400]} />
    </g>
  );
};

interface HangSpec {
  left: string;
  width: string;
  thread: number;
  variant: number;
  duration: number;
}

const HANGING: HangSpec[] = [
  { left: "32.5%", width: "clamp(30px, 3.1vw, 54px)", thread: 30, variant: 0, duration: 5.2 },
  { left: "37%", width: "clamp(24px, 2.4vw, 42px)", thread: 96, variant: 1, duration: 4.4 },
  { left: "62%", width: "clamp(26px, 2.6vw, 46px)", thread: 70, variant: 2, duration: 5.6 },
  { left: "66.5%", width: "clamp(32px, 3.3vw, 58px)", thread: 18, variant: 3, duration: 6 },
];

const EasterScene: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => {
  const rFar = ridge({ seed: 71, base: 450, amp: 90, scale: 2.4, shape: x => 50 * Math.exp(-Math.pow((x - 1180) / 260, 2)) });
  const rField = ridge({ seed: 83, base: 512, amp: 44, scale: 1.8 });
  const rNear = ridge({
    seed: 91,
    base: 582,
    amp: 22,
    scale: 2,
    shape: x => 66 * Math.pow(Math.abs(x - 800) / 800, 2.2),
  });

  // Rader i rapsfältet som följer kullens form
  const rows = range(9).map(i => {
    const off = 10 + i * 11;
    const pts = range(41).map(k => {
      const x = -40 + k * 42;
      return `${x} ${(rField.y(x) + off).toFixed(1)}`;
    });
    return `M${pts.join(" L")}`;
  });

  // Grästuvor på ängen
  const rnd = seeded(5);
  const tufts = range(120).map(() => {
    const x = rnd() * 1600;
    const y = rNear.y(x) + 8 + rnd() * 30;
    const h = 10 + rnd() * 16;
    return `M${(x - 5).toFixed(1)} ${y.toFixed(1)} Q ${(x - 6).toFixed(1)} ${(y - h * 0.6).toFixed(1)} ${(x - 9).toFixed(1)} ${(y - h).toFixed(1)} M ${x.toFixed(1)} ${y.toFixed(1)} Q ${(x + 1).toFixed(1)} ${(y - h * 0.7).toFixed(1)} ${(x + 2).toFixed(1)} ${(y - h * 1.2).toFixed(1)} M ${(x + 5).toFixed(1)} ${y.toFixed(1)} Q ${(x + 7).toFixed(1)} ${(y - h * 0.6).toFixed(1)} ${(x + 11).toFixed(1)} ${(y - h * 0.9).toFixed(1)}`;
  });

  const eggBase = (i: number): ShadeColors =>
    [
      { base: p[300], shade: p[500], light: p[100], rim: p[400] },
      { base: "#fffdf7", shade: "#f0dfc4", light: "#ffffff" },
      { base: p[400], shade: p[600], light: p[200], rim: p[500] },
      { base: p[200], shade: p[400], light: "#fffbe8", rim: p[300] },
    ][i % 4];
  const eggs: { x: number; s: number; rot: number; pattern: EggPattern }[] = [
    { x: 196, s: 0.5, rot: -12, pattern: "zigzag" },
    { x: 236, s: 0.4, rot: 14, pattern: "dots" },
    { x: 470, s: 0.36, rot: -6, pattern: "flowers" },
    { x: 1176, s: 0.4, rot: 10, pattern: "bands" },
    { x: 1462, s: 0.55, rot: -8, pattern: "flowers" },
    { x: 1512, s: 0.42, rot: 16, pattern: "zigzag" },
  ];

  return (
    <>
      <ToonDefs />
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="ea-meadow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={p[200]} />
            <stop offset="18%" stopColor={p[300]} />
            <stop offset="100%" stopColor={mix(p[300], p[400], 0.6)} />
          </linearGradient>
        </defs>
      </svg>
      <div
        className="hol-sky-fill"
        style={{
          background: `radial-gradient(circle at 89% 30%, #ffffff 0%, ${p[50]} 9%, transparent 38%), linear-gradient(180deg, ${p[100]} 0%, ${mix(p[100], p[200], 0.4)} 40%, ${p[200]} 75%, ${mix(p[200], p[300], 0.6)} 100%)`,
        }}
      />
      <Rays left="14vw" top="calc(30vh - 75vw)" color="rgba(255,255,255,0.16)" />
      <div className="hol-orb" style={{ left: "89%", top: "30%", width: "clamp(110px, 11vw, 190px)" }}>
        <svg viewBox="-100 -100 200 200">
          <defs>
            <radialGradient id="ea-sun" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="42%" stopColor="#ffffff" />
              <stop offset="50%" stopColor="#fffbe6" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle r="100" fill="url(#ea-sun)" />
        </svg>
      </div>

      {/* Moln */}
      <div className="hol-drift-cloud" style={{ top: "9%", width: "clamp(120px, 12vw, 220px)", animationDuration: "140s", animationDelay: "-30s" }}>
        <Cloud p={p} />
      </div>
      <div className="hol-drift-cloud" style={{ top: "20%", width: "clamp(80px, 8vw, 150px)", animationDuration: "170s", animationDelay: "-100s" }}>
        <Cloud p={p} />
      </div>
      <div className="hol-drift-cloud" style={{ top: "4%", width: "clamp(90px, 9vw, 160px)", animationDuration: "200s", animationDelay: "-150s" }}>
        <Cloud p={p} />
      </div>

      {/* Påskkärringen */}
      <div className="hol-flight" style={{ top: "13%", width: "clamp(150px, 15vw, 260px)", animationDuration: "40s", animationDelay: "-6s" }}>
        <div className="hol-flight-inner">
          <Witch p={p} />
        </div>
      </div>

      <Layer delay={0}>
        <path d={rFar.d} fill={mix(p[300], p[200], 0.4)} />
        {range(16).map(i => {
          const x = 60 + i * 97 + (i % 3) * 13;
          if (x > 1080 && x < 1300) return null;
          const y = rFar.y(x) + 4;
          const r = 9 + (i % 4) * 3;
          return <path key={i} d={`${circlePath(x, y - r, r)} ${circlePath(x - r * 0.7, y - r * 0.6, r * 0.7)} ${circlePath(x + r * 0.7, y - r * 0.5, r * 0.75)}`} fill={mix(p[400], p[300], 0.45)} />;
        })}
        <Cottage x={1170} y={rFar.y(1170) + 6} s={0.7} p={p} />
      </Layer>
      <Layer delay={140}>
        <path d={rField.d} fill={p[400]} />
        {rows.map((d, i) => (
          <path key={i} d={d} stroke={i % 2 ? p[500] : p[300]} strokeOpacity={i % 2 ? 0.7 : 0.8} strokeWidth={2 + i * 0.5} fill="none" />
        ))}
      </Layer>
      <Layer delay={280}>
        <path d={rNear.d} fill="url(#ea-meadow)" />
        <path d={rNear.d} fill={p[400]} opacity="0.5" transform="translate(0 30)" />
        <path d={tufts.join(" ")} stroke={LEAF.base} strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.8" />
        {/* Påskliljor */}
        {[
          [70, 1, -8],
          [112, 0.8, 6],
          [150, 1.1, -4],
          [40, 0.7, 10],
          [1400, 0.9, 6],
          [1440, 1.15, -6],
          [1492, 0.8, 8],
          [1545, 1.05, -8],
          [1580, 0.75, 4],
        ].map(([x, s, lean], i) => (
          <Daffodil key={i} x={x} y={rNear.y(x) + 34} s={s} lean={lean} p={p} delay={-i * 0.7} />
        ))}
        {eggs.map((e, i) => (
          <Egg
            key={i}
            x={e.x}
            y={rNear.y(e.x) + 18}
            s={e.s}
            rot={e.rot}
            base={eggBase(i)}
            ink={i % 2 ? p[500] : "#ffffff"}
            accent={i % 2 ? p[300] : p[600]}
            pattern={e.pattern}
          />
        ))}
        <Bunny x={330} y={rNear.y(330) + 32} s={1} p={p} />
        <Chick x={420} y={rNear.y(420) + 30} s={0.9} p={p} shell />
        <Chick x={460} y={rNear.y(460) + 30} s={0.75} p={p} flip />
        <Bunny x={1300} y={rNear.y(1300) + 32} s={0.82} p={p} flip egg={false} />
        <Chick x={1226} y={rNear.y(1226) + 30} s={0.8} p={p} />
      </Layer>

      <ParticleField kind="feathers" dark={dark} density={0.8} />
      {HANGING.map((h, i) => (
        <div key={i} className="hol-hang hol-hide-md" style={{ left: h.left, width: h.width, animationDelay: `${300 + parseFloat(h.left) * 12}ms` }}>
          <div className="hol-hang-swing" style={{ animationDuration: `${h.duration}s`, animationDelay: `${-h.duration * 0.4}s` }}>
            <HangingEgg p={p} thread={h.thread} variant={h.variant} />
          </div>
        </div>
      ))}
      <Grain opacity={0.035} />
    </>
  );
};

export default EasterScene;
